import React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Hint } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS_FACTORIAL_TIPS } from 'config/Vars'
import { getDate, getMonth, getYear } from 'date-fns'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import TotalHoursHint from 'modules/project_management/components/hints/myProjects/TotalHoursHint'
import MyProjectsTable from 'modules/project_management/components/tables/MyProjectsTable'
import { originSection } from 'modules/project_management/lib/utils'
import { RouteContextParams } from 'modules/project_management/pages/MyProjectsRootPage'

const FOUR_STEPS_HINT = 4
const FIVE_STEPS_HINT = 5

const MyProjects = () => {
  const navigate = useNavigate()
  const {
    projectsStore,
    employeeIdNumber,
    employeeLocationId,
    canRegisterFlexibleTracking,
    hasTimeOffModule,
    trackingViewRoute,
  } = useOutletContext<RouteContextParams>()

  const [hasGlobalTimeRecords, setHasGlobalTimeRecords] = React.useState<boolean>(false)

  const [hintStep, setHintStep] = React.useState<number>(0)
  const [withProjectsHintsAreHidden, setWithProjectsHintsAreHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideMyProjectsWithProjectsHint
  )
  const [withoutProjectsHintsAreHidden, setWithoutProjectsHintsAreHidden] =
    useLocalStorage<boolean>(LOCAL_STORAGE_KEYS_FACTORIAL_TIPS.hideMyProjectsWithoutProjectsHint)

  useEffect(() => {
    track(trackingEvents.projectManagement.flexibleTracking.visitMyProjectsTrackingSection)

    if (hintStep < 2 && (!withoutProjectsHintsAreHidden || !withProjectsHintsAreHidden))
      track(
        hasGlobalTimeRecords
          ? trackingEvents.projectManagement.flexibleTracking.hints.visitAddProjectHintWithProjects
          : trackingEvents.projectManagement.flexibleTracking.hints
              .visitAddProjectHintWithoutProjects,
        {
          origin: originSection(),
        }
      )
  }, [hintStep, withoutProjectsHintsAreHidden, withProjectsHintsAreHidden])

  const buildWeekNavigation = (week: Date) => {
    const newWeek = {
      year: getYear(week),
      month: getMonth(week) + 1,
      day: getDate(week),
      employeeId: employeeIdNumber,
    }
    return navigate(trackingViewRoute(newWeek))
  }

  // Track hints
  const hasEmployeeGlobalTimeRecords = (hasGlobalTimeRecords: boolean) => {
    setHasGlobalTimeRecords(hasGlobalTimeRecords)
  }

  const handleHintClose = () => {
    hasGlobalTimeRecords
      ? setWithProjectsHintsAreHidden(true)
      : setWithoutProjectsHintsAreHidden(true)
    track(
      hasGlobalTimeRecords
        ? trackingEvents.projectManagement.flexibleTracking.hints.closeAddProjectHintWithProjects
        : trackingEvents.projectManagement.flexibleTracking.hints
            .closeAddProjectHintWithoutProjects,
      {
        origin: originSection(),
      }
    )
  }

  const handleHintNext = (nextStep: number) => {
    setHintStep(nextStep)
    track(
      hasGlobalTimeRecords
        ? trackingEvents.projectManagement.flexibleTracking.hints.nextProjectHintWithProjects
        : trackingEvents.projectManagement.flexibleTracking.hints.nextProjectHintWithoutProjects,
      {
        origin: originSection(),
      }
    )
  }

  return (
    <Hint.Popover.Provider
      steps={hasGlobalTimeRecords ? FIVE_STEPS_HINT : FOUR_STEPS_HINT}
      isHidden={!!withoutProjectsHintsAreHidden || !!withProjectsHintsAreHidden}
      onChange={handleHintNext}
      onClose={handleHintClose}
      onFinish={handleHintClose}
    >
      <MyProjectsTable
        projectsStore={projectsStore}
        hasEmployeeGlobalTimeRecords={hasEmployeeGlobalTimeRecords}
        hasGlobalTimeRecords={hasGlobalTimeRecords}
        hasTimeOffModule={hasTimeOffModule}
        canRegisterFlexibleTracking={canRegisterFlexibleTracking}
        buildWeekNavigation={buildWeekNavigation}
        employeeLocationId={employeeLocationId}
      />
      <Box position="relative" alignSelf="center" paddingRight="s300">
        <TotalHoursHint stepNumber={hasGlobalTimeRecords ? FIVE_STEPS_HINT : FOUR_STEPS_HINT} />
      </Box>
    </Hint.Popover.Provider>
  )
}
export default MyProjects
